import type { MapPoiCategory } from "./services/map.types";

export type MapRegion = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

export const MAP_UI_LIGHT = {
  primary: "#4614BB",
  primaryLight: "#5F33E1",
  accent: "#EC4899",
  userPin: "#3B82F6",
  selectedPin: "#2D0D78",
  mapBackground: "#E8EDF2",
} as const;

export const MAP_UI_DARK = {
  primary: "#9B7AE8",
  primaryLight: "#B49AF0",
  accent: "#F472B6",
  userPin: "#60A5FA",
  selectedPin: "#C4B5FD",
  mapBackground: "#1A1D24",
} as const;

export function getMapUi(isDarkMode: boolean) {
  return isDarkMode ? MAP_UI_DARK : MAP_UI_LIGHT;
}

export const INITIAL_REGION: MapRegion = {
  latitude: -5.3,
  longitude: -39.3,
  latitudeDelta: 3,
  longitudeDelta: 3,
};

export const CATEGORY_COLORS: Record<MapPoiCategory, string> = {
  educacao: MAP_UI_LIGHT.primaryLight,
  escola_publica: MAP_UI_LIGHT.primary,
  saude: "#10B981",
  comunidade: MAP_UI_LIGHT.accent,
  acessibilidade: "#0EA5E9",
};

export const CATEGORY_LABELS: Record<MapPoiCategory, string> = {
  educacao: "Educação",
  escola_publica: "Escola pública",
  saude: "Saúde",
  comunidade: "Comunidade",
  acessibilidade: "Acessibilidade",
};

export const NEAR_ME_FILTER = "near_me" as const;
export type MapOverviewFilter = MapPoiCategory | typeof NEAR_ME_FILTER;

export const MAP_FILTER_CATEGORIES: MapPoiCategory[] = [
  "saude",
  "educacao",
  "escola_publica",
  "comunidade",
  "acessibilidade",
];

/** Tiles Leaflet/OSM sem API key (Carto passou a exigir chave). */
export const OSM_TILE_LAYERS = {
  standard: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    maxZoom: 19,
    subdomains: "abc",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  dark: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    maxZoom: 19,
    subdomains: "abc",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    /** Escurece os tiles OSM via CSS no MapLeaflet */
    invertTiles: true,
  },
} as const;

export type OsmTileStyle = keyof typeof OSM_TILE_LAYERS;

export const MAP_COPY = {
  searchPlaceholder: "Buscar locais de Libras…",
  nearMe: "Perto de mim",
  legendTitle: "Legenda do mapa",
  youAreHere: "Você está aqui",
  legendSelected: "Local selecionado",
  osmAttribution: "© OpenStreetMap · Leaflet",
  locationPermissionTitle: "Permitir localização",
  locationPermissionBody:
    "Para mostrar locais perto de você, precisamos da sua localização.",
  allowLocation: "Permitir",
  cancel: "Cancelar",
  viewDetails: "Ver detalhes",
  nearYou: "Perto de você",
  mostAccessed: "Mais acessados",
  recentAccessed: "Acessados recentemente",
  searchResults: "Resultados",
  poiDetails: "Detalhes do local",
  addressLabel: "Endereço",
  hoursLabel: "Horário",
  phoneLabel: "Telefone",
  emailLabel: "E-mail",
  openInMaps: "Abrir no mapa",
  sharePlace: "Compartilhar",
  openMapTitle: "Abrir em",
  openMapDescription: "Escolha um app de mapas",
  contactTitle: "Contato",
  contactDescription: "Como deseja entrar em contato?",
  dactylologyLabel: "Dactilologia",
  viewDactylology: "Ver dactilologia",
  openDactylologyFullScreen: "Abrir em tela cheia",
  loading: "Carregando…",
  noResults: "Nenhum local encontrado",
  back: "Voltar",
} as const;

export function regionToZoom(longitudeDelta: number): number {
  return Math.max(0, Math.min(20, Math.round(Math.log2(360 / longitudeDelta))));
}

export function zoomToLongitudeDelta(zoom: number): number {
  return 360 / 2 ** zoom;
}
